// Runtime configuration.
//
// By default the app talks to the SAME origin it was served from — i.e. the
// Oracle VM (Caddy proxies /peerjs and /ice to the PeerServer). This means a
// production build needs zero env vars.
//
// For the Phase-0 spike you can point at the public PeerJS cloud + Google STUN
// by setting VITE_USE_PUBLIC=1, so the UI works before any backend exists.

const usePublic = import.meta.env.VITE_USE_PUBLIC === "1";

// Debug: `?relay=1` forces all media through TURN, to verify the relay path
// works even on networks where direct P2P would otherwise succeed.
export const forceRelay =
  new URLSearchParams(location.search).get("relay") === "1";

export const iceTransportPolicy: RTCIceTransportPolicy = forceRelay
  ? "relay"
  : "all";

export interface PeerHostConfig {
  host: string;
  port: number;
  path: string;
  secure: boolean;
  key?: string;
}

export const peerHost: PeerHostConfig = usePublic
  ? // Public PeerJS cloud broker — no backend required.
    { host: "0.peerjs.com", port: 443, path: "/", secure: true, key: "peerjs" }
  : // Self-hosted on the VM, same origin, proxied by Caddy at /peerjs.
    {
      host: location.hostname,
      port: location.protocol === "https:" ? 443 : Number(location.port) || 9000,
      path: "/peerjs",
      secure: location.protocol === "https:",
      // Must match the PeerServer key ("peerjs" default). Passing undefined
      // would override PeerJS's own default and break the handshake.
      key: "peerjs",
    };

// Fallback ICE servers used when the /ice endpoint is unreachable
// (e.g. the public-broker spike). STUN-only: direct P2P works for most
// networks; TURN relay is provided by the self-hosted /ice endpoint.
export const fallbackIceServers: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

// Fetch time-limited TURN credentials from the self-hosted endpoint.
// Returns the fallback list if unavailable.
export async function getIceServers(): Promise<RTCIceServer[]> {
  if (usePublic) return fallbackIceServers;
  try {
    const res = await fetch("/ice", { cache: "no-store" });
    if (!res.ok) throw new Error(`/ice ${res.status}`);
    const data = (await res.json()) as { iceServers: RTCIceServer[] };
    if (Array.isArray(data.iceServers) && data.iceServers.length) {
      return data.iceServers;
    }
  } catch (err) {
    console.warn("Falling back to public STUN:", err);
  }
  return fallbackIceServers;
}
